import { Input } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import QRCode from 'react-qr-code'

import { TotpSetup } from '@/api/type-response'
import CopyIcon from '@/assets/icons/copy.svg?react'
import { message } from '@/components'
import { Button } from '@/components/base/Button'
import { Loading } from '@/components/base/Loading'
import { Modal } from '@/components/base/Modal'
import { MfaCodeInput } from '@/components/mfa/MfaCodeInput'
import { MfaService } from '@/services/mfa-service'
import { getErrorCodeMessage } from '@/utils/error'

interface MfaSetupWizardModalProps {
  initialStep?: 1 | 2 | 3
  onClose: () => void
  onSuccess: () => void
}

type SetupStep = 1 | 2 | 3

export function MfaSetupWizardModal({
  initialStep = 1,
  onClose,
  onSuccess,
}: MfaSetupWizardModalProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<SetupStep>(initialStep)
  const [setup, setSetup] = useState<TotpSetup | null>(null)
  const [loading, setLoading] = useState(initialStep === 1)
  const [verifyCode, setVerifyCode] = useState('')
  const [verifyError, setVerifyError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (initialStep !== 1) {
      return
    }

    let cancelled = false

    MfaService.setup()
      .then((data) => {
        if (!cancelled) {
          setSetup(data)
        }
      })
      .catch((error) => {
        console.error(error)
        if (!cancelled) {
          message.error(t('mfa.setup.failed'))
          onClose()
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
    // Only fetch once when the wizard opens at step 1.
    // Do not add onClose/t — inline callbacks change every parent re-render
    // and would repeatedly call setup(), replacing the QR code.
  }, [initialStep])

  const accountLabel = setup ? `${setup.issuer} / ${setup.account}` : ''

  const handleCopy = async (text: string, successKey: string) => {
    await navigator.clipboard.writeText(text)
    message.success(t(successKey))
  }

  const handleCopyRecoveryCodes = async () => {
    if (!setup) {
      return
    }
    await navigator.clipboard.writeText(setup.recoveryCodes.join('\n'))
    message.success(t('mfa.setup.recoveryCopied'))
  }

  const handleRestartSetup = async () => {
    setLoading(true)
    setVerifyCode('')
    setVerifyError('')
    try {
      const data = await MfaService.setup()
      setSetup(data)
      setStep(1)
    } catch (error) {
      console.error(error)
      message.error(t('mfa.setup.failed'))
    } finally {
      setLoading(false)
    }
  }

  const handleEnable = async (submittedCode?: string) => {
    const verificationCode = submittedCode ?? verifyCode
    if (verificationCode.length !== 6 || submitting) {
      return
    }

    setSubmitting(true)
    setVerifyError('')

    try {
      const result = await MfaService.enable(verificationCode)
      if ('code' in result && result.code !== 0) {
        const errorMessage = getErrorCodeMessage(result.code, t)
        setVerifyCode('')
        setVerifyError(errorMessage)
        message.error(errorMessage)
        return
      }

      message.success(t('mfa.setup.success'))
      onSuccess()
    } finally {
      setSubmitting(false)
    }
  }

  const renderStepContent = () => {
    if (initialStep === 1 && loading) {
      return (
        <div className="flex justify-center items-center min-h-[280px]">
          <Loading />
        </div>
      )
    }

    if (initialStep === 1 && !setup) {
      return null
    }

    if (step === 1 && setup) {
      return (
        <div className="flex flex-col gap-6">
          <p className="t-sm text-secondary">{t('mfa.setup.step1Description')}</p>
          <div className="flex gap-6 items-start">
            <div className="flex-shrink-0 p-4 bg-panel-white border border-neutral-200 rounded-lg">
              <QRCode value={setup.otpAuthURL} size={160} />
            </div>
            <div className="flex flex-col gap-4 flex-1 min-w-0">
              <div>
                <div className="t-sb text-primary mb-2">{t('mfa.setup.accountName')}</div>
                <Input
                  value={accountLabel}
                  readOnly
                  className="bg-neutral-50 text-secondary cursor-default"
                />
              </div>
              <div>
                <div className="t-sb text-primary mb-2">{t('mfa.setup.secretKey')}</div>
                <div className="flex gap-2">
                  <Input value={setup.secret} readOnly className="bg-neutral-50 text-secondary" />
                  <button
                    type="button"
                    className="flex-shrink-0 w-11 h-11 flex items-center justify-center border border-neutral-200 rounded-lg hover:bg-neutral-50"
                    onClick={() => handleCopy(setup.secret, 'mfa.setup.secretCopied')}
                  >
                    <CopyIcon className="w-5 h-5 text-secondary" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (step === 2 && setup) {
      return (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <p className="t-sb text-primary">{t('mfa.setup.step2Heading')}</p>
            <button
              type="button"
              className="flex items-center gap-1 t-sm text-btn-secondary whitespace-nowrap"
              onClick={handleCopyRecoveryCodes}
            >
              <CopyIcon className="w-4 h-4" />
              {t('mfa.setup.copyCodes')}
            </button>
          </div>
          <p className="t-sm text-secondary">{t('mfa.setup.step2Description')}</p>
          <div className="grid grid-cols-2 gap-3">
            {setup.recoveryCodes.map((code) => (
              <div
                key={code}
                className="px-4 py-3 rounded-lg bg-neutral-50 border border-neutral-200 t-sb text-primary text-center"
              >
                {code}
              </div>
            ))}
          </div>
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-4">
        <p className="t-sm text-secondary">{t('mfa.setup.step3Description')}</p>
        {initialStep === 3 && <p className="t-xs text-secondary">{t('mfa.setup.resumeHint')}</p>}
        <MfaCodeInput
          value={verifyCode}
          onChange={(value) => {
            setVerifyCode(value)
            if (verifyError) {
              setVerifyError('')
            }
          }}
          onComplete={handleEnable}
          disabled={submitting}
          hasError={!!verifyError}
          autoFocus
        />
        {verifyError && <p className="t-sm text-error">{verifyError}</p>}
      </div>
    )
  }

  const renderFooter = () => {
    if (step === 1) {
      return (
        <Modal.Footer>
          <Button type="outline" onClick={onClose} className="min-w-[88px]">
            {t('common.confirmModalCancel')}
          </Button>
          <Button
            type="secondary"
            onClick={() => setStep(2)}
            disabled={loading || !setup}
            className="min-w-[88px]"
          >
            {t('mfa.setup.next')}
          </Button>
        </Modal.Footer>
      )
    }

    if (step === 2) {
      return (
        <Modal.Footer>
          <Button type="outline" onClick={() => setStep(1)} className="min-w-[88px]">
            {t('mfa.setup.back')}
          </Button>
          <Button type="secondary" onClick={() => setStep(3)} className="min-w-[88px]">
            {t('mfa.setup.next')}
          </Button>
        </Modal.Footer>
      )
    }

    return (
      <Modal.Footer>
        {initialStep === 1 ? (
          <Button type="outline" onClick={() => setStep(2)} className="min-w-[88px]">
            {t('mfa.setup.back')}
          </Button>
        ) : (
          <Button
            type="outline"
            onClick={handleRestartSetup}
            disabled={loading}
            className="min-w-[120px]"
          >
            {t('mfa.setup.restart')}
          </Button>
        )}
        <Button
          type="secondary"
          onClick={() => handleEnable()}
          disabled={submitting || verifyCode.length !== 6}
          className="min-w-[160px]"
        >
          {t('mfa.setup.verifyEnable')}
        </Button>
      </Modal.Footer>
    )
  }

  const stepTitle =
    step === 1
      ? t('mfa.setup.step1Title')
      : step === 2
        ? t('mfa.setup.step2Title')
        : t('mfa.setup.step3Title')

  return (
    <Modal className={step === 1 ? 'w-[720px]' : 'w-[640px]'} open={true} onClose={onClose}>
      <Modal.Header>{stepTitle}</Modal.Header>
      <Modal.Body scrollable>{renderStepContent()}</Modal.Body>
      {renderFooter()}
    </Modal>
  )
}
